var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import TabComponent from "@widget/list/Tabs/TabComponent";
import FormField from '../inputs/FormField';
import Label from '../inputs/Label';
import Input from '../inputs/Input';
import PasswordInput from '../inputs/PasswordInput';
import Checkbox from '../inputs/Checkbox';
import Select from '../inputs/Select';
import classNames from 'classnames';
import TextArea from '../inputs/TextArea';
import Conditional from "../../../helper/Conditional/ConditionalComponent";
import DatePicker from "../inputs/DateTimePicker/DatePicker";
import DateTimePicker from "../inputs/DateTimePicker/DateTimePicker";
import TimePicker from "../inputs/DateTimePicker/TimePicker";
import Button from "@widget/buttons/Button";
import DateRangePicker from "../inputs/DateTimePicker/DateRangePicker";
import TimeRangePicker from "../inputs/DateTimePicker/TimeRangePicker";
import TimeRangeSliderPicker from "../inputs/DateTimePicker/TimeRangeSliderPicker";
import { UXPContext } from '@core/context/Context';
import { hasValue, toBool, toDate, toStr } from '@utils';
import { endOfDay, format, isValid, startOfDay, } from 'date-fns';
import { SkeletonLoader } from '@widget/loaders';
import { WizardStepIndicator } from './WizardStepIndicator';
import InfoButton from '@widget/popups/InfoButton';
import { Collapse } from '@widget/other/Collapse';
/**
 * Detects field dependencies by parsing function bodies for formData references
 * Returns array of field names that the field depends on
 */
function detectFieldDependencies(field) {
    var _a, _b;
    const deps = new Set();
    // Helper to extract field names from function source
    const extractFieldNames = (fnSource) => {
        // Pattern 1: data.fieldName or data?.fieldName
        const dotPattern = /data\.(\w+)|data\?\.(\w+)/g;
        let match;
        while ((match = dotPattern.exec(fnSource)) !== null) {
            const fieldName = match[1] || match[2];
            if (fieldName)
                deps.add(fieldName);
        }
        // Pattern 2: data['fieldName'] or data["fieldName"]
        const bracketPattern = /data\[['"](\w+)['"]\]/g;
        while ((match = bracketPattern.exec(fnSource)) !== null) {
            if (match[1])
                deps.add(match[1]);
        }
    };
    // Check show function
    if (field.show) {
        try {
            extractFieldNames(field.show.toString());
        }
        catch (e) {
            // If parsing fails, assume depends on all fields (safe fallback)
            console.warn(`Failed to detect dependencies for field ${field.name} show function`);
        }
    }
    // Check getOptions function
    if (field.getOptions) {
        try {
            extractFieldNames(field.getOptions.toString());
        }
        catch (e) {
            console.warn(`Failed to detect dependencies for field ${field.name} getOptions function`);
        }
    }
    // Check getPaginatedOptions function
    if (field.getPaginatedOptions) {
        try {
            extractFieldNames(field.getPaginatedOptions.toString());
        }
        catch (e) {
            console.warn(`Failed to detect dependencies for field ${field.name} getPaginatedOptions function`);
        }
    }
    // Check custom validation function
    if ((_a = field.validate) === null || _a === void 0 ? void 0 : _a.customValidateFunction) {
        try {
            extractFieldNames(field.validate.customValidateFunction.toString());
        }
        catch (e) {
            console.warn(`Failed to detect dependencies for field ${field.name} validation function`);
        }
    }
    // Check conditional required function
    if (typeof ((_b = field.validate) === null || _b === void 0 ? void 0 : _b.required) === 'function') {
        try {
            extractFieldNames(field.validate.required.toString());
        }
        catch (e) {
            console.warn(`Failed to detect dependencies for field ${field.name} required function`);
        }
    }
    // Check renderField function
    if (field.renderField) {
        try {
            extractFieldNames(field.renderField.toString());
        }
        catch (e) {
            console.warn(`Failed to detect dependencies for field ${field.name} renderField function`);
        }
    }
    return Array.from(deps);
}
const MemoizedFormField = React.memo((props) => {
    var _a;
    const { field, value, error, formData, onChange } = props;
    const { name, type, label, validate, renderField } = field;
    const placeholder = field.placeholder || "";
    // State for async options resolution
    const [resolvedOptions, setResolvedOptions] = React.useState([]);
    const [isLoadingOptions, setIsLoadingOptions] = React.useState(false);
    // Detect dependencies for getOptions to trigger re-fetch when relevant fields change
    const optionsDeps = React.useMemo(() => {
        if (field.type === 'select' && field.getOptions) {
            const deps = field.dependsOn || detectFieldDependencies(field);
            return deps.map(dep => formData[dep]);
        }
        return [];
    }, [field, formData]);
    // Handle async getOptions for select fields
    React.useEffect(() => {
        if (field.type !== 'select' || !field.getOptions)
            return;
        let cancelled = false;
        setIsLoadingOptions(true);
        try {
            const result = field.getOptions(formData);
            // Handle Promise (async service call)
            if (result instanceof Promise) {
                result
                    .then((opts) => {
                    if (!cancelled) {
                        setResolvedOptions(Array.isArray(opts) ? opts : []);
                        setIsLoadingOptions(false);
                    }
                })
                    .catch((err) => {
                    if (!cancelled) {
                        console.error(`Failed to load options for field ${field.name}:`, err);
                        setResolvedOptions([]);
                        setIsLoadingOptions(false);
                    }
                });
            }
            // Handle synchronous array
            else if (Array.isArray(result)) {
                setResolvedOptions(result);
                setIsLoadingOptions(false);
            }
            else {
                setResolvedOptions([]);
                setIsLoadingOptions(false);
            }
        }
        catch (err) {
            console.error(`Error calling getOptions for field ${field.name}:`, err);
            setResolvedOptions([]);
            setIsLoadingOptions(false);
        }
        return () => {
            cancelled = true;
        };
    }, [field.type, field.getOptions, field.name, formData, ...optionsDeps]);
    // Set static options if provided
    React.useEffect(() => {
        if (field.type === 'select' && field.options && !field.getOptions) {
            setResolvedOptions(Array.isArray(field.options) ? field.options : []);
        }
    }, [field.type, field.options, field.getOptions]);
    let input = null;
    switch (field.type) {
        case "checkbox":
        case "toggle":
            input = _jsx(Checkbox, { checked: toBool(value), onChange: val => onChange(val, name), label: (field === null || field === void 0 ? void 0 : field.checkboxLabel) || label, type: type == "checkbox" ? "bordered" : "switch-line" });
            break;
        case "select":
            const selectedOptionLabel = field === null || field === void 0 ? void 0 : field.selectedOptionLabel;
            input = _jsx(Select, { selected: value, onChange: val => onChange(val, name), options: (field === null || field === void 0 ? void 0 : field.getPaginatedOptions)
                    ? (max, lastToken, args) => field.getPaginatedOptions(formData, max, lastToken, args)
                    : resolvedOptions, placeholder: placeholder, selectedLabel: typeof selectedOptionLabel === 'function'
                    ? (selected) => selectedOptionLabel(formData, selected)
                    : selectedOptionLabel, labelField: hasValue(field === null || field === void 0 ? void 0 : field.labelField) ? field.labelField : 'label', valueField: hasValue(field === null || field === void 0 ? void 0 : field.valueField) ? field.valueField : 'value' });
            break;
        case "date":
            const date = hasValue(value) ? new Date(value) : null;
            const formattedDate = (!!date && isValid(date)) ? format(date, 'yyyy/MM/dd') : '';
            input = _jsx(DatePicker, { title: "", date: formattedDate, onChange: val => {
                    onChange(format(val, "yyyy/MM/dd"), name);
                } });
            break;
        case "datetime":
            input = _jsx(DateTimePicker, { title: "", datetime: value, onChange: val => {
                    onChange(val, name);
                } });
            break;
        case "time":
            input = _jsx(TimePicker, { title: "", time: value, onChange: val => onChange(format(val, "HH:mm:ss"), name) });
            break;
        case "daterange":
            input = _jsx(DateRangePicker, { title: "", startDate: (value === null || value === void 0 ? void 0 : value.startDate) || '', endDate: (value === null || value === void 0 ? void 0 : value.endDate) || '', onChange: (s, e) => {
                    const sd = isValid(s) ? startOfDay(typeof s === 'string' ? new Date(s) : s).toISOString() : null;
                    const ed = isValid(e) ? endOfDay(typeof e === 'string' ? new Date(e) : e).toISOString() : null;
                    onChange({
                        startDate: sd,
                        endDate: ed
                    }, name);
                } });
            break;
        case "timerange":
            input = _jsx(TimeRangePicker, { title: "", startTime: (value === null || value === void 0 ? void 0 : value.startTime) || '', endTime: (value === null || value === void 0 ? void 0 : value.endTime) || '', onChange: (start, end) => {
                    onChange({
                        startTime: start,
                        endTime: end
                    }, name);
                } });
            break;
        case "timerangeslider":
            input = _jsx(TimeRangeSliderPicker, { title: "", startTime: (value === null || value === void 0 ? void 0 : value.startTime) || '', endTime: (value === null || value === void 0 ? void 0 : value.endTime) || '', onChange: (start, end) => {
                    onChange({
                        startTime: start,
                        endTime: end
                    }, name);
                } });
            break;
        case 'textarea':
        case "json":
            input = _jsx(TextArea, { value: value, onChange: val => onChange(val, name) });
            break;
        case "number":
            input = _jsx(Input, { type: 'number', value: value, onChange: val => {
                    let float = parseFloat(val);
                    onChange(isNaN(float) ? 0 : float, name);
                }, placeholder: placeholder });
            break;
        case 'readonly':
            input = _jsx(Label, { children: toStr(value) });
            break;
        case 'password':
            input = _jsx(PasswordInput, { value: value, onChange: val => onChange(val, name), placeholder: placeholder });
            break;
        default:
            const _t = (hasValue(type) && ['text', 'email'].includes(type)) ? type : 'text';
            input = _jsx(Input, { type: _t, value: value, onChange: val => onChange(val, name), placeholder: placeholder });
            break;
    }
    if (type == 'hidden')
        return null;
    if (type == 'spacer')
        return _jsx("div", { className: "uxpcore_dynamicform__spacer" });
    const infoPosition = (_a = field.infoPosition) !== null && _a !== void 0 ? _a : 'label';
    const fieldContent = typeof renderField == 'function' ? renderField(formData, (val) => onChange(val, name)) : input;
    return _jsxs(FormField, { children: [_jsxs("div", { className: "uxpcore_dynamicform__field-header", children: [_jsxs(Label, { icon: field.icon, children: [label, (typeof (validate === null || validate === void 0 ? void 0 : validate.required) === 'function' ? validate.required(formData) : validate === null || validate === void 0 ? void 0 : validate.required) ? _jsx("span", { className: "uxpcore_dynamicform__required-mark", children: " *" }) : null] }), field.info && infoPosition === 'label' && _jsx(InfoButton, { content: field.info, size: "small" })] }), field.description && (_jsx("div", { className: "uxpcore_dynamicform__field-description", children: field.description })), field.info && infoPosition === 'input'
                ? _jsxs("div", { className: "uxpcore_dynamicform__field-input-row", children: [fieldContent, _jsx(InfoButton, { content: field.info })] })
                : fieldContent, _jsx(FormError, { formErrors: { [name]: error }, field: name })] }, name);
}, (prevProps, nextProps) => {
    const field = prevProps.field;
    // 1. Always check own value and error
    if (prevProps.value !== nextProps.value)
        return false;
    if (prevProps.error !== nextProps.error)
        return false;
    // 2. Check if static options changed (for select fields)
    if (field.type === 'select' && !field.getOptions && !field.getPaginatedOptions) {
        // Compare options arrays by reference - parent should provide stable reference or new reference when changed
        if (prevProps.field.options !== nextProps.field.options) {
            return false; // Options changed, must re-render
        }
    }
    // 3. Get dependencies (explicit or auto-detected)
    const deps = field.dependsOn || detectFieldDependencies(field);
    // 4. If no dependencies, can skip re-render
    if (deps.length === 0)
        return true;
    // 5. Check if any dependency changed
    for (const dep of deps) {
        if (prevProps.formData[dep] !== nextProps.formData[dep]) {
            return false; // Dependency changed, must re-render
        }
    }
    // 6. No changes detected
    return true; // Skip re-render
});
MemoizedFormField.displayName = 'MemoizedFormField';
/**
 * @export
 * This component provides a dynamic form component
 * Developer can pass a json structure and it will create a form component
 */
const DynamicForm = React.forwardRef((props, ref) => {
    var _a, _b;
    const [formData, setFormData] = React.useState({});
    const [isDataLoaded, setIsDataLoaded] = React.useState(false);
    const [formErrors, setFormErrors] = React.useState({});
    const [processing, setProcessing] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [tabs, setTabs] = React.useState([]);
    const [selectedTab, setSelectedTab] = React.useState(null);
    const formDataRef = React.useRef({});
    const context = React.useContext(UXPContext);
    const renderStyle = (!!(props === null || props === void 0 ? void 0 : props.renderOptions) && !!((_a = props === null || props === void 0 ? void 0 : props.renderOptions) === null || _a === void 0 ? void 0 : _a.renderStyle)) ? (_b = props === null || props === void 0 ? void 0 : props.renderOptions) === null || _b === void 0 ? void 0 : _b.renderStyle : 'standard';
    // Expose methods to parent component
    React.useImperativeHandle(ref, () => ({
        submit: handleSubmitExternal,
        cancel: handleCancel,
        validate: validateForm,
        getFormData: () => formDataRef.current,
        setFormData: updateFormData,
        getErrors: () => formErrors,
        clearErrors: () => setFormErrors({}),
        setErrors: setErrorsExternal,
        clearFieldError: clearFieldErrorExternal,
        isProcessing: () => processing,
        goToNext: onClickNext,
        goToPrevious: onClickPrevious,
        getWizardState: () => {
            if (renderStyle !== 'wizard' || tabs.length === 0)
                return null;
            const currentIndex = tabs.findIndex(t => t.id === selectedTab);
            const currentStep = currentIndex >= 0 ? currentIndex : 0;
            return {
                currentStep: currentStep + 1, // 1-indexed for display
                totalSteps: tabs.length,
                isFirstStep: currentStep === 0,
                isLastStep: currentStep === tabs.length - 1
            };
        }
    }));
    // Reseed only when fields or their seeded values actually change — parents recreate
    // formStructure inline on every render; resetting on identity wipes user edits.
    const structureSignature = React.useMemo(() => {
        try {
            return (props.formStructure || []).map(section => {
                const allFields = section.sections ? section.sections.flatMap(s => s.fields || []) : section.fields;
                return (allFields || []).map(f => { var _a; return `${f.name}:${f.type}:${JSON.stringify((_a = f.value) !== null && _a !== void 0 ? _a : null)}`; }).join(',');
            }).join('|');
        }
        catch (_a) {
            return String(Date.now()); // unserializable value — fall back to reseed-every-render (old behavior)
        }
    }, [props.formStructure]);
    React.useEffect(() => {
        updateFormDataWithDefaults();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [structureSignature, props.isLoading]);
    React.useEffect(() => {
        if (renderStyle !== 'standard') {
            const _tabs = (props.formStructure || []).map((section, i) => {
                // Collect all fields for validation/error tracking — from flat fields or sub-sections
                const allFields = section.sections
                    ? section.sections.flatMap(s => s.fields)
                    : section.fields;
                return {
                    id: `tab-${i + 1}`,
                    label: (section === null || section === void 0 ? void 0 : section.title) || `Section ${i + 1}`,
                    content: _jsx(_Fragment, {}),
                    fields: allFields.filter(f => f.type !== 'spacer').map(f => f.name),
                    index: i,
                    visibleFields: allFields.filter(f => f.type !== 'hidden'),
                    columns: section.columns || 1,
                    maxColumnWidth: section.maxColumnWidth,
                    show: section.show,
                    section
                };
            });
            setTabs(_tabs);
        }
        else {
            setTabs([]);
            setSelectedTab(null);
        }
    }, [props.formStructure, renderStyle, formData]);
    React.useEffect(() => {
        const _tab = getActiveTab(tabs);
        setSelectedTab(_tab);
    }, [tabs, formErrors]);
    // Notify parent of wizard state changes
    React.useEffect(() => {
        if (props.onWizardStateChange) {
            if (renderStyle === 'wizard' && tabs.length > 0 && selectedTab) {
                const currentIndex = tabs.findIndex(t => t.id === selectedTab);
                const currentStep = currentIndex >= 0 ? currentIndex : 0;
                const wizardState = {
                    currentStep: currentStep + 1,
                    totalSteps: tabs.length,
                    isFirstStep: currentStep === 0,
                    isLastStep: currentStep === tabs.length - 1
                };
                props.onWizardStateChange(wizardState);
            }
            else {
                props.onWizardStateChange(null);
            }
        }
    }, [renderStyle, tabs, selectedTab, props.onWizardStateChange]);
    React.useEffect(() => { setLoading(props.isLoading || false); }, [props.isLoading]);
    React.useEffect(() => {
        if (!isDataLoaded && Object.keys(formData).length > 0) {
            setIsDataLoaded(true);
        }
    }, [formData, isDataLoaded]);
    React.useEffect(() => {
        console.log('formerror', formErrors);
    }, [formErrors]);
    function updateFormData(data) {
        setFormData(data);
        formDataRef.current = data;
    }
    function updateFormDataWithDefaults() {
        let newData = {};
        for (const section of (props === null || props === void 0 ? void 0 : props.formStructure) || []) {
            const allFields = section.sections
                ? section.sections.flatMap(s => s.fields)
                : section.fields;
            for (const field of allFields) {
                let type = field.type || "text";
                if (type === 'spacer')
                    continue;
                let value = field.value;
                switch (type) {
                    case "checkbox":
                    case "toggle":
                        if (!value) {
                            value = false;
                        }
                        else if (typeof value != 'boolean') {
                            value = !!value;
                        } // string 'false' will return true
                        break;
                    case "number":
                        if (!value) {
                            value = 0;
                        }
                        else if (typeof value == 'number' || typeof value == 'string') {
                            // assume float value is provided 
                            let float = typeof value == 'number' ? value : parseFloat(value);
                            value = (isNaN(float) || !isFinite(float)) ? 0 : float;
                        }
                        break;
                    case "date":
                    case "datetime": {
                        // value can be a Date instance (DateTimePicker onChange emits Date objects)
                        if (value instanceof Date) {
                            value = isValid(value) ? value.toISOString() : null;
                        }
                        else if (hasValue(value) && typeof value == 'string') {
                            const date = new Date(value);
                            if (isValid(date))
                                value = date.toISOString();
                            else
                                value = null;
                        }
                        else
                            value = null;
                        break;
                    }
                    case "daterange": {
                        if (!!value) {
                            value = {
                                startDate: (!!(value === null || value === void 0 ? void 0 : value.startDate)) ? toDate(value === null || value === void 0 ? void 0 : value.startDate) : '',
                                endDate: (!!(value === null || value === void 0 ? void 0 : value.endDate)) ? toDate(value === null || value === void 0 ? void 0 : value.endDate) : ''
                            };
                        }
                        else
                            value = { startDate: '', endDate: '' };
                        break;
                    }
                    case "timerange": {
                        if (!!value) {
                            value = {
                                startTime: (!!(value === null || value === void 0 ? void 0 : value.startTime)) ? toDate(value === null || value === void 0 ? void 0 : value.startTime) : '',
                                endTime: (!!(value === null || value === void 0 ? void 0 : value.endTime)) ? toDate(value === null || value === void 0 ? void 0 : value.endTime) : ''
                            };
                        }
                        else
                            value = { startTime: '', endTime: '' };
                        break;
                    }
                    case "timerangeslider": {
                        if (!!value) {
                            value = {
                                startTime: (!!(value === null || value === void 0 ? void 0 : value.startTime)) ? toDate(value === null || value === void 0 ? void 0 : value.startTime) : '',
                                endTime: (!!(value === null || value === void 0 ? void 0 : value.endTime)) ? toDate(value === null || value === void 0 ? void 0 : value.endTime) : ''
                            };
                        }
                        else
                            value = { startTime: '', endTime: '' };
                        break;
                    }
                    case 'json':
                        value = value;
                    default:
                        if (!value)
                            value = "";
                        break;
                }
                Object.assign(newData, { [field.name]: value });
            }
        }
        updateFormData(newData);
    }
    // Validate entire form
    function validateForm(_data) {
        return __awaiter(this, void 0, void 0, function* () {
            let errors = {};
            let isValidForm = true;
            const _formData = !!_data ? _data : formDataRef.current;
            for (const section of props.formStructure) {
                const allFields = section.sections
                    ? section.sections.flatMap(s => s.fields)
                    : section.fields;
                for (const field of allFields) {
                    if (field.type === 'spacer')
                        continue;
                    let key = field.name;
                    let val = _formData[key];
                    let { valid, error } = yield validateField(key, val, _formData);
                    if (!valid) {
                        errors[key] = error;
                        isValidForm = false;
                    }
                }
            }
            setFormErrors(errors);
            return isValidForm;
        });
    }
    // External submit handler
    function handleSubmitExternal() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (processing)
                    return;
                setProcessing(true);
                const _formData = (props === null || props === void 0 ? void 0 : props.beforeSubmit) ? yield props.beforeSubmit(formDataRef.current) : formDataRef.current;
                const isValid = yield validateForm(_formData);
                if (!isValid) {
                    setProcessing(false);
                    return;
                }
                yield props.onSubmit(_formData);
                setProcessing(false);
            }
            catch (e) {
                console.error('Unable to submit. Error: ', e);
                setProcessing(false);
                throw e; // Re-throw for external handling
            }
        });
    }
    // submit event
    function handleSubmit(e) {
        return __awaiter(this, void 0, void 0, function* () {
            e.preventDefault();
            e.stopPropagation();
            yield handleSubmitExternal();
        });
    }
    const handleCancel = () => {
        var _a;
        formDataRef.current = {};
        updateFormDataWithDefaults();
        (_a = props === null || props === void 0 ? void 0 : props.onCancel) === null || _a === void 0 ? void 0 : _a.call(props);
    };
    const setErrorsExternal = (errors) => {
        setFormErrors((prev) => (Object.assign(Object.assign({}, prev), errors)));
    };
    const clearFieldErrorExternal = (fieldName) => {
        setFormErrors((prev) => {
            const newErrors = Object.assign({}, prev);
            if (Array.isArray(fieldName)) {
                fieldName.forEach(name => delete newErrors[name]);
            }
            else {
                delete newErrors[fieldName];
            }
            return newErrors;
        });
    };
    // change event
    const handleChange = (value, key) => __awaiter(void 0, void 0, void 0, function* () {
        const allFields = props.formStructure.flatMap(s => ([...s.fields]));
        // definition 
        const fieldDef = allFields.find(f => f.name === key);
        // format value before change 
        if (fieldDef && (fieldDef === null || fieldDef === void 0 ? void 0 : fieldDef.formatter)) {
            value = fieldDef.formatter(value);
        }
        updateFormData(Object.assign(Object.assign({}, formDataRef.current), { [key]: value }));
        // setFormData(prev => ({ ...prev, [key]: value }));
        // formDataRef.current = { ...formDataRef.current, [key]: value }
        let { valid, error } = yield validateField(key, value, formDataRef.current);
        console.log('validating', value, key, valid, error);
        // remove erros if valid 
        if (valid) {
            setFormErrors((prev) => {
                const newErrors = Object.assign({}, prev);
                delete newErrors[key];
                console.log('validating', newErrors, key);
                return newErrors;
            });
        }
        // add if not
        else
            setFormErrors((prev) => (Object.assign(Object.assign({}, prev), { [key]: error })));
    });
    function validateField(key, value, _formData) {
        return __awaiter(this, void 0, void 0, function* () {
            const allFields = props.formStructure.flatMap(s => ([...s.fields]));
            let field = allFields.find(f => f.name == key);
            if (!field)
                return { valid: false, error: 'Unknown field' };
            let validator = field.validate;
            if (!validator)
                return { valid: true };
            let { required, allowEmptyString, minLength, maxLength, regExp, allowZero, allowNegative, minVal, maxVal, customValidateFunction } = validator;
            const isRequired = typeof required === 'function' ? required(_formData) : !!required;
            if (isRequired) {
                if (!value)
                    return { valid: false, error: 'Field is required' };
                let type = typeof value;
                if (type == 'string') {
                    if (!allowEmptyString && value.trim().length == 0)
                        return { valid: false, error: 'Field is required' };
                    if (minLength) {
                        if (value.trim().length < minLength)
                            return { valid: false, error: `Minimum lenth should be ${minLength}` };
                    }
                    if (maxLength) {
                        if (value.trim().length > maxLength)
                            return { valid: false, error: `Maximum length should be ${maxLength}` };
                    }
                }
                if (type == 'number') {
                    if (!allowNegative && Number(value) < 0)
                        return { valid: false, error: 'Negative values are not allowed' };
                    if (!allowZero && value == 0)
                        return { valid: false, error: 'Value should be graget than zero(0)' };
                    if (minVal && Number(value) < minVal)
                        return { valid: false, error: `Value should be greater than ${minVal}` };
                    if (maxVal && Number(value) > maxVal)
                        return { valid: false, error: `Value should be lesser than ${minVal}` };
                }
            }
            if (value && regExp && !regExp.test(value))
                return { valid: false, error: 'Invalid format' };
            if (customValidateFunction)
                return yield Promise.resolve(customValidateFunction(value, _formData));
            return { valid: true };
        });
    }
    function getActiveTab(tabs) {
        var _a, _b;
        const firstError = (_a = Object.keys(formErrors)) === null || _a === void 0 ? void 0 : _a[0];
        if (Object.keys(formErrors).length > 0 && !!firstError) {
            const tab = tabs.find(t => t.fields.includes(firstError));
            if (tab)
                return `tab-${tab.index + 1}`;
        }
        if (!!selectedTab)
            return selectedTab;
        return ((_b = tabs === null || tabs === void 0 ? void 0 : tabs[0]) === null || _b === void 0 ? void 0 : _b.id) || null;
    }
    function renderField(formField) {
        return (_jsx(MemoizedFormField, { field: formField, value: formData[formField.name], error: formErrors[formField.name], formData: formData, onChange: handleChange }, formField.name));
    }
    function renderFormSectionContent(cols, visibleFields, maxColumnWidth) {
        const _maxWidth = maxColumnWidth || '30rem';
        return _jsx(FormRow, { cols: cols, maxColumnWidth: _maxWidth, children: visibleFields.map((field, k) => {
                return _jsx(Conditional, { visible: field.show ? field.show(formData) : true, children: _jsx("div", { className: 'uxpcore_dynamicform__form-field', children: renderField(field) }) }, k);
            }) });
    }
    function renderSectionBody(section) {
        if (section.sections) {
            return section.sections.map((sub, i) => (_jsx(FormSection, { title: sub.title, show: sub.show ? sub.show(formData) : true, seperator: sub.seperator, collapsible: sub.collapsible, defaultExpanded: sub.defaultExpanded, children: renderFormSectionContent(sub.columns || 1, sub.fields.filter(f => f.type !== 'hidden'), sub.maxColumnWidth) }, i)));
        }
        return renderFormSectionContent(section.columns || 1, section.fields.filter(f => f.type !== 'hidden'), section.maxColumnWidth);
    }
    function renderStandardForm() {
        return (props.formStructure || []).map((section, i) => {
            return _jsx(FormSection, { title: section.title, show: (section === null || section === void 0 ? void 0 : section.show) ? section.show(formData) : true, seperator: section.seperator, collapsible: section.collapsible, defaultExpanded: section.defaultExpanded, children: renderSectionBody(section) }, i);
        });
    }
    function extractTabStyles() {
        var _a, _b;
        const { tabBackgroundColor, tabTextColor, activeTabBackgroundColor, activeTabTextColor, errorTabBackgroundColor, errorTabTextColor, disabledTabBackgroundColor, disabledTabTextColor } = props.renderOptions;
        return {
            tabBackgroundColor: hasValue(tabBackgroundColor) ? tabBackgroundColor : null,
            tabTextColor: hasValue(tabTextColor) ? tabTextColor : null,
            selectedTabBackgroundColor: hasValue(activeTabBackgroundColor) ? activeTabBackgroundColor : null,
            selectedTabTextColor: hasValue(activeTabTextColor) ? activeTabTextColor : null,
            tabHighlightBackgroundColor: hasValue(errorTabBackgroundColor) ? errorTabBackgroundColor : (_a = context === null || context === void 0 ? void 0 : context.theme) === null || _a === void 0 ? void 0 : _a.primaryBGColor,
            tabHighlightTextColor: hasValue(errorTabTextColor) ? errorTabTextColor : (_b = context === null || context === void 0 ? void 0 : context.theme) === null || _b === void 0 ? void 0 : _b.toastErrorBGColor,
            disabledTabBackgroundColor: hasValue(disabledTabBackgroundColor) ? disabledTabBackgroundColor : null,
            disabledTabTextColor: hasValue(disabledTabTextColor) ? disabledTabTextColor : null
        };
    }
    function renderTabbedForm() {
        var _a, _b;
        const _tabs = (tabs || [])
            .filter((extended) => {
            // Only include tabs where show is undefined or returns true
            return extended.show ? extended.show(formData) : true;
        })
            .map((extended) => {
            return {
                id: extended.id,
                label: extended.label,
                content: renderSectionBody(extended.section),
                highlight: Object.keys(formErrors).some((a) => extended.fields.includes(a)),
                disabled: renderStyle == 'wizard' && extended.id !== selectedTab
            };
        });
        // Wizard mode: use step indicator instead of tabs
        if (renderStyle === 'wizard') {
            const wizardSteps = _tabs.map(tab => ({
                id: tab.id,
                label: typeof tab.label === 'string' ? tab.label : String(tab.label),
                hasError: tab.highlight || false
            }));
            // Get completed steps (all steps before current step)
            const currentIndex = _tabs.findIndex(t => t.id === selectedTab);
            const completedSteps = currentIndex > 0
                ? _tabs.slice(0, currentIndex).map(t => t.id)
                : [];
            return (_jsxs(_Fragment, { children: [_jsx(WizardStepIndicator, { steps: wizardSteps, currentStepId: selectedTab, completedSteps: completedSteps }), _jsx("div", { className: "uxpcore_dynamicform__wizard-content", children: (_a = _tabs.find(t => t.id === selectedTab)) === null || _a === void 0 ? void 0 : _a.content })] }));
        }
        // Tabs mode: use TabComponent
        return _jsx(TabComponent, { tabs: _tabs, onChangeTab: setSelectedTab, selected: selectedTab, direction: (_b = props === null || props === void 0 ? void 0 : props.renderOptions) === null || _b === void 0 ? void 0 : _b.direction, position: 'top', styles: extractTabStyles() });
    }
    function generateForm() {
        switch (renderStyle) {
            case 'tabs':
            case 'wizard':
                return renderTabbedForm();
            default:
                return renderStandardForm();
        }
    }
    function onClickPrevious() {
        var _a, _b;
        const currentIndex = tabs.findIndex(t => t.id == selectedTab);
        if (currentIndex <= 0) {
            setSelectedTab(((_a = tabs === null || tabs === void 0 ? void 0 : tabs[0]) === null || _a === void 0 ? void 0 : _a.id) || null);
        }
        setSelectedTab((((_b = tabs === null || tabs === void 0 ? void 0 : tabs[currentIndex - 1]) === null || _b === void 0 ? void 0 : _b.id) || null));
    }
    function onClickNext() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const currentTab = tabs.find(t => t.id == selectedTab);
            const fields = currentTab.fields || [];
            // Validate all fields concurrently
            const validationResults = yield Promise.all(fields.map((field) => __awaiter(this, void 0, void 0, function* () {
                const val = formData[field];
                const { valid, error } = yield validateField(field, val, formData);
                return { field, valid, error };
            })));
            // Reduce the results
            const { isValid, errors } = validationResults.reduce((res, { field, valid, error }) => {
                res.isValid = res.isValid && valid;
                if (!valid)
                    res.errors[field] = error;
                return res;
            }, { isValid: true, errors: {} });
            if (fields.length == 0 || isValid) {
                setSelectedTab((((_a = tabs === null || tabs === void 0 ? void 0 : tabs[currentTab.index + 1]) === null || _a === void 0 ? void 0 : _a.id) || null));
            }
            else {
                setFormErrors(errors);
            }
        });
    }
    function renderSkeletonForm() {
        return (props.formStructure || []).map((section, i) => {
            const visibleFields = section.fields.filter(f => f.type !== 'hidden');
            const cols = section.columns || 1;
            return (_jsxs("div", { className: classNames('uxpcore_dynamicform__form-section', 'uxpcore_dynamicform__skeleton', {
                    'uxpcore_dynamicform__form-section--has-seperator': section === null || section === void 0 ? void 0 : section.seperator
                }), children: [section.title && (_jsx("div", { className: 'uxpcore_dynamicform__form-section-label', children: _jsx(SkeletonLoader, { width: "150px", height: "20px", additionalStyle: { marginBottom: '10px' } }) })), _jsx("div", { className: 'uxpcore_dynamicform__form-section-content', children: _jsx(FormRow, { cols: cols, maxColumnWidth: section.maxColumnWidth, children: visibleFields.map((_, k) => (_jsx("div", { className: 'uxpcore_dynamicform__form-field', children: _jsxs(FormField, { children: [_jsx(SkeletonLoader, { width: "120px", height: "14px", additionalStyle: { marginBottom: '8px' } }), _jsx(SkeletonLoader, { width: "100%", height: "40px", additionalStyle: { borderRadius: '4px' } })] }) }, k))) }) })] }, i));
        });
    }
    function renderSkeletonByStyle() {
        switch (renderStyle) {
            case 'tabs':
            case 'wizard':
                // For tabs/wizard, render skeleton tabs + skeleton fields
                return (_jsxs("div", { className: "uxpcore_dynamicform__skeleton", children: [_jsx("div", { style: { display: 'flex', gap: '10px', marginBottom: '20px' }, children: props.formStructure.map((_, i) => (_jsx(SkeletonLoader, { width: "100px", height: "40px", additionalStyle: { borderRadius: '4px' } }, i))) }), props.formStructure.length > 0 && (() => {
                            const firstSection = props.formStructure[0];
                            const visibleFields = firstSection.fields.filter(f => f.type !== 'hidden');
                            const cols = firstSection.columns || 1;
                            return (_jsx("div", { className: 'uxpcore_dynamicform__form-section-content', children: _jsx(FormRow, { cols: cols, maxColumnWidth: firstSection.maxColumnWidth, children: visibleFields.map((_, k) => (_jsx("div", { className: 'uxpcore_dynamicform__form-field', children: _jsxs(FormField, { children: [_jsx(SkeletonLoader, { width: "120px", height: "14px", additionalStyle: { marginBottom: '8px' } }), _jsx(SkeletonLoader, { width: "100%", height: "40px", additionalStyle: { borderRadius: '4px' } })] }) }, k))) }) }));
                        })()] }));
            default:
                // Standard form skeleton
                return renderSkeletonForm();
        }
    }
    function renderButtons() {
        var _a, _b, _c, _d, _e, _f;
        // Don't render buttons if hideButtons is true
        if (props.hideButtons)
            return null;
        const btnSize = props.buttonSize;
        if (renderStyle == 'wizard') {
            const isLastStep = tabs.findIndex(t => t.id == selectedTab) == (tabs.length - 1);
            const isFirstStep = tabs.findIndex(t => t.id == selectedTab) == 0;
            if (isLastStep) {
                return _jsxs(_Fragment, { children: [_jsx(Button, { title: ((_a = props === null || props === void 0 ? void 0 : props.renderOptions) === null || _a === void 0 ? void 0 : _a.previousButtonLabel) ? (_b = props === null || props === void 0 ? void 0 : props.renderOptions) === null || _b === void 0 ? void 0 : _b.previousButtonLabel : context.$L('uxp-core.text.previous'), onClick: onClickPrevious, type: "button", leftIcon: 'fas chevron-left', className: 'dynamic-form-secondary-btn', variant: 'secondary', size: btnSize }), !(props === null || props === void 0 ? void 0 : props.hideCancelButton) ?
                            _jsx(Button, { title: props.cancelButtonLabel ? props.cancelButtonLabel : context.$L('uxp-core.text.cancel'), onClick: handleCancel, type: "reset", leftIcon: (props === null || props === void 0 ? void 0 : props.cancelButtonLabel) || 'fas times', className: 'dynamic-form-secondary-btn', variant: 'secondary', size: btnSize })
                            : null, _jsx(Button, { title: props.submitButtonLabel ? props.submitButtonLabel : context.$L('uxp-core.text.submit'), loadingTitle: props.submitButtonLoadingLabel ? props.submitButtonLoadingLabel : context.$L('uxp-core.text.submiting'), onClick: () => { }, loading: processing, type: "submit", leftIcon: (props === null || props === void 0 ? void 0 : props.submitButtonIcon) || 'fas save', className: 'dynamic-form-primary-btn', variant: 'primary', size: btnSize })] });
            }
            else {
                return _jsxs(_Fragment, { children: [!isFirstStep ?
                            _jsx(Button, { title: ((_c = props === null || props === void 0 ? void 0 : props.renderOptions) === null || _c === void 0 ? void 0 : _c.previousButtonLabel) ? (_d = props === null || props === void 0 ? void 0 : props.renderOptions) === null || _d === void 0 ? void 0 : _d.previousButtonLabel : context.$L('uxp-core.text.previous'), onClick: onClickPrevious, type: "button", leftIcon: 'fas chevron-left', className: 'dynamic-form-secondary-btn', variant: 'secondary', size: btnSize })
                            : null, _jsx(Button, { title: ((_e = props === null || props === void 0 ? void 0 : props.renderOptions) === null || _e === void 0 ? void 0 : _e.nextButtonLabel) ? (_f = props === null || props === void 0 ? void 0 : props.renderOptions) === null || _f === void 0 ? void 0 : _f.nextButtonLabel : context.$L('uxp-core.text.next'), onClick: onClickNext, type: "button", rightIcon: 'fas chevron-right', className: 'dynamic-form-primary-btn', variant: 'primary', size: btnSize })] });
            }
        }
        else {
            return _jsxs(_Fragment, { children: [!(props === null || props === void 0 ? void 0 : props.hideCancelButton) ?
                        _jsx(Button, { title: props.cancelButtonLabel ? props.cancelButtonLabel : context.$L('uxp-core.text.cancel'), onClick: handleCancel, type: "reset", leftIcon: (props === null || props === void 0 ? void 0 : props.cancelButtonIcon) || 'fas times', className: 'dynamic-form-secondary-btn', variant: 'secondary', size: btnSize })
                        : null, _jsx(Button, { title: props.submitButtonLabel ? props.submitButtonLabel : context.$L('uxp-core.text.submit'), loadingTitle: props.submitButtonLoadingLabel ? props.submitButtonLoadingLabel : context.$L('uxp-core.text.submiting'), onClick: () => { }, loading: processing, type: "submit", leftIcon: (props === null || props === void 0 ? void 0 : props.submitButtonIcon) || 'fas save', className: 'dynamic-form-primary-btn', variant: 'primary', size: btnSize })] });
        }
    }
    return (_jsx(_Fragment, { children: _jsx("div", { className: "uxpcore_dynamicform__container", style: (props === null || props === void 0 ? void 0 : props.formContainerStyles) || {}, children: _jsxs("form", { onSubmit: handleSubmit, className: 'uxpcore_dynamicform__form', children: [_jsx("div", { className: "uxpcore_dynamicform__body", children: (loading || !isDataLoaded) ? renderSkeletonByStyle() : generateForm() }), !props.hideButtons && (_jsx("div", { className: "uxpcore_dynamicform__footer", children: renderButtons() }))] }) }) }));
});
const FormError = (props) => {
    const { formErrors, field } = props;
    return _jsx(_Fragment, { children: formErrors && formErrors[field] && formErrors[field].trim().length > 0 &&
            _jsx("div", { className: "uxpcore_dynamicform__field-error", children: formErrors[field] }) });
};
const FormSection = (props) => {
    const { show, title, seperator, children, collapsible = false, defaultExpanded = true } = props;
    return _jsx(Conditional, { visible: show, children: _jsx("div", { className: classNames('uxpcore_dynamicform__form-section', { 'uxpcore_dynamicform__form-section--has-seperator': seperator }), children: collapsible && hasValue(title)
                ? _jsx(Collapse, { title: title, defaultExpanded: defaultExpanded, children: _jsx("div", { className: 'uxpcore_dynamicform__form-section-content', children: children }) })
                : _jsxs(_Fragment, { children: [_jsx(Conditional, { visible: hasValue(title), children: _jsx("div", { className: 'uxpcore_dynamicform__form-section-label', children: title }) }), _jsx("div", { className: 'uxpcore_dynamicform__form-section-content', children: children })] }) }) });
};
const FormRow = (props) => {
    const maxWidth = props.maxColumnWidth || '25rem';
    return _jsx("div", { className: 'uxpcore_dynamicform__form-row', style: { gridTemplateColumns: `repeat(${props.cols}, minmax(0, ${maxWidth}))` }, children: props.children });
};
export default DynamicForm;
